import { type ReactNode, useEffect, useMemo } from "react";

import clsx from "clsx";

import type {
  GetSalesMetricsReportV4,
  GetSalesMetricsReportV4Variables,
} from "@north-beam/nb-client-side";
import type { SalesMetricRow } from "@north-beam/nb-common";

import { Spinner } from "@components/v2";
import { useQueryMetadata } from "@components/v2/modals";
import {
  useSalesV4Context,
  useTableRowFilters,
} from "@features/sales-v4/hooks";
import { type TableState } from "@features/sales-v4/types";
import { selectedTabToLevel } from "@features/sales-v4/utils";
import { SALES_METRICS_REPORT_V4 } from "@gql/sales-page-v4";
import { useNorthbeamQuery } from "@hooks/http";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import { MetricValue } from "@shared/metric-value";

export type TooltipRowMetadata = {
  metricId: string;
  name?: ReactNode;
  calculate?: (data: Record<string, unknown>) => number | string;
  format?: SalesMetricV2["format"];
};

type Props = {
  row: SalesMetricRow;
  tableState: TableState;
  setIsLoading: (isLoading: boolean) => void;
  metricIds: string[];
  tooltipRowMetadata: TooltipRowMetadata[];
};

export const TooltipMetricsTable = ({
  row,
  tableState,
  setIsLoading,
  metricIds,
  tooltipRowMetadata,
}: Props) => {
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRangeInterval, metrics } = useSalesV4Context();
  const { addQueryMetadata } = useQueryMetadata();

  const {
    dimensionIds,
    metricFilters,
    breakdownFilters,
    campaignFilters,
    adsetFilters,
    adFilters,
    advancedSearch,
    isSummary,
  } = useTableRowFilters({
    tableState,
    row,
  });

  const { data, loading } = useNorthbeamQuery<
    GetSalesMetricsReportV4,
    GetSalesMetricsReportV4Variables
  >(SALES_METRICS_REPORT_V4, {
    variables: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity: "daily",
      metricIds,
      dimensionIds,
      metricFilters,
      advancedSearch,
      breakdownFilters,
      campaignFilters,
      adsetFilters,
      adFilters,
      isSummary,
    },
    onCompleted(data) {
      addQueryMetadata({
        context: "Revenue",
        topic: "Tooltip Metrics Table",
        metadata: data.me.salesMetricsReportV4.queryMetadata,
      });
    },
  });

  useEffect(() => {
    setIsLoading(loading);
  }, [loading, setIsLoading]);

  const metricsData = useMemo(() => {
    const mData = data?.me.salesMetricsReportV4.actual[0]?.metrics ?? {};
    return tooltipRowMetadata.reduce<Record<string, number>>(
      (acc, { metricId, calculate }) => ({
        ...acc,
        [metricId]: calculate?.(mData) ?? mData[metricId] ?? 0,
      }),
      {},
    );
  }, [data, tooltipRowMetadata]);

  if (loading) {
    return (
      <div
        className={clsx(
          "p-4 bg-v2-secondary rounded-lg flex items-center justify-center",
          "w-full h-full",
        )}
      >
        <Spinner className="text-v2-cosmic/400" />
      </div>
    );
  }

  return (
    <div className="border border-v2-tertiary rounded bg-v2-primary">
      {tooltipRowMetadata.map(({ metricId, name, format }) => {
        const metric = metrics.find((m) => m.id === metricId);
        return (
          <div key={metricId} className="h-10 flex items-stretch">
            <div className="border-r border-b border-v2-tertiary px-4 flex items-center w-[180px] gap-2">
              {name ?? metric?.name}
            </div>
            <div className="flex-1 px-4 border-b border-v2-tertiary flex items-center justify-end">
              {typeof metricsData[metricId] === "number" ? (
                <MetricValue
                  value={metricsData[metricId]}
                  format={format ?? metric?.format ?? "integer"}
                />
              ) : (
                metricsData[metricId]
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
